import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

const titleLines = [
  { text: '让智能触手可及', bold: true, large: true },
  { text: '让数据赋能决策', bold: true, large: true },
  { text: '以技术驱动产业升级', bold: false, large: false },
];

export default function MissionStatement() {
  const { ref: sectionRef, visible } = useInView(0.15);
  const { ref: gridRef, visible: gridVisible } = useInView(0.1);

  return (
    <section className="py-10 md:py-20 lg:py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        {/* Title block */}
        <div ref={sectionRef} className="text-center mb-10 md:mb-16">
          {/* Label */}
          <span
            className="text-xs font-semibold text-gray-400 tracking-widest uppercase inline-block"
            style={{
              transition: 'opacity 0.6s ease, transform 0.6s ease',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(-16px)',
            }}
          >
            OUR MISSION
          </span>

          {/* Animated title lines */}
          <div className="mt-5 md:mt-8 space-y-2 md:space-y-4">
            {titleLines.map((line, i) => (
              <div
                key={i}
                className="overflow-hidden"
                style={{ perspective: '600px' }}
              >
                <h2
                  className={`leading-tight inline-block ${
                    line.bold ? 'font-bold text-gray-900' : 'font-light text-gray-500'
                  } ${
                    line.large
                      ? 'text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl'
                      : 'text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl'
                  }`}
                  style={{
                    transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${i * 160 + 100}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${i * 160 + 100}ms`,
                    opacity: visible ? 1 : 0,
                    transform: visible
                      ? 'translateY(0) rotateX(0deg)'
                      : 'translateY(60px) rotateX(25deg)',
                  }}
                >
                  {line.text}
                </h2>
              </div>
            ))}
          </div>

          {/* Animated underline */}
          <div className="mt-6 md:mt-8 flex justify-center">
            <div className="relative h-0.5 w-24 bg-gray-100 overflow-hidden rounded-full">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-teal-400 via-cyan-300 to-teal-500 rounded-full"
                style={{
                  transition: 'width 1s cubic-bezier(0.22,1,0.36,1) 600ms',
                  width: visible ? '100%' : '0%',
                }}
              />
            </div>
          </div>
        </div>

        {/* Two-column text blocks */}
        <div ref={gridRef} className="grid md:grid-cols-2 gap-6 md:gap-12 mt-8 md:mt-16">
          {[
            {
              title: '公司定位与发展',
              text: '龙创未来自成立以来,始终聚焦 AI 与智能硬件、软件与服务的深度融合。公司依托自主研发的 AI 算法平台、端侧 AI 芯片与行业应用产品,在人工智能、大模型应用、工业数字化、智慧城市等领域积累了丰富的项目经验,并与多家高校、科研院所及产业链伙伴建立长期合作,持续推动技术成果转化与场景落地。',
              dir: -1,
            },
            {
              title: '愿景与使命',
              text: '龙创未来以「技术驱动创新、场景创造价值」为理念,以「让智能触手可及、让数据赋能决策」为使命。我们坚持自主可控与开放合作并重,在保障数据安全与业务合规的前提下,帮助客户构建可持续演进的智能化能力,共同迎接智能时代的机遇与挑战。',
              dir: 1,
            },
          ].map((item, i) => (
            <div
              key={i}
              className="space-y-2.5 md:space-y-4"
              style={{
                transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${i * 180}ms, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${i * 180}ms`,
                opacity: gridVisible ? 1 : 0,
                transform: gridVisible
                  ? 'translateX(0)'
                  : `translateX(${item.dir * 60}px)`,
              }}
            >
              {/* Title with left accent bar */}
              <div className="flex items-center gap-3">
                <span
                  className="inline-block w-1 rounded-full bg-teal-400"
                  style={{
                    transition: `height 0.6s cubic-bezier(0.22,1,0.36,1) ${i * 180 + 300}ms`,
                    height: gridVisible ? '1.5rem' : '0',
                  }}
                />
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600 leading-[1.85] md:leading-relaxed pl-4">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
